import { createFileRoute, Link } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CreateGroupButton } from '@/admin/groups/CreateGroupButton';
import { adminGroupFilterQuery } from '@/admin/groups/groupFilters';
import { GROUP_TYPE_KEYS, GROUP_TYPES } from '@/admin/groups/groupTypes';
import { useAdminGroupCountries } from '@/admin/groups/useAdminGroupCountries';
import { createTablePage } from '@/admin/ui/createTablePage';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import i18n from '~/i18n';
import type { FilterField } from '~/styles/tables/filterandSettingsStyles';

type AdminGroupItem = components['schemas']['AdminGroupItem'];

// Legacy's admin datatables page twenty at a time.
const PAGE_SIZE = 20;
const EMPTY = '—';

const columnHelper = createColumnHelper<AdminGroupItem>();

function formatUtcDate(value: string | null): string {
  if (!value) return EMPTY;
  // Pinned to UTC: the date filters build their bounds in UTC, so a cell in
  // the viewer's zone would disagree with its own filter near midnight.
  return new Date(value).toLocaleDateString(undefined, { timeZone: 'UTC' });
}

function LocationCell({ group }: { group: AdminGroupItem }) {
  const countries = useAdminGroupCountries();
  const place = [group.city, group.state].filter(Boolean).join(', ');
  const country = group.country
    ? (countries.find((c) => c.code === group.country)?.name ?? group.country)
    : '';
  return <>{[place, country].filter(Boolean).join(' ') || EMPTY}</>;
}

function OwnerCell({ owners }: { owners: AdminGroupItem['owners'] }) {
  const { t } = useTranslation();
  const [first, ...rest] = owners;
  if (!first) return <>{t('admin.groups.noOwner')}</>;
  return (
    <>
      {first.first_name} {first.last_name}
      {rest.length > 0 && (
        <small>
          {' '}
          {t('admin.groups.andMore', {
            count: rest.length,
            interpolation: { escapeValue: false },
          })}
        </small>
      )}
    </>
  );
}

// Row clicks open the record, so each link keeps its own click.
const stop = (e: React.MouseEvent) => e.stopPropagation();

function ActionsCell({ group }: { group: AdminGroupItem }) {
  const { t } = useTranslation();
  const { me } = useCurrentUser();
  return (
    <Actions onClick={stop}>
      {me &&
        group.owners.map((owner) => (
          <Link
            key={owner.id}
            to="/members/$memberId/messages/compose/"
            params={{ memberId: String(me.id) }}
            search={{ r: owner.id }}
            title={owner.display_name}
          >
            {t('admin.groups.messageOwner', {
              name: owner.display_name,
              interpolation: { escapeValue: false },
            })}
          </Link>
        ))}
      <Link to="/groups/$groupName/" params={{ groupName: group.slug }}>
        {t('admin.groups.view')}
      </Link>
      <Link to="/admin/groups/$groupId/" params={{ groupId: String(group.id) }}>
        {t('admin.groups.open')}
      </Link>
    </Actions>
  );
}

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

// Column ids are the left-hand side of `sortKeys`; Location, Owner and the
// actions are unsortable, as in legacy.
const columns = [
  columnHelper.accessor('id', {
    id: 'id',
    header: () => i18n.t('admin.common.id'),
  }),
  // Already unslashed by the API.
  columnHelper.accessor((row) => row.name, {
    id: 'name',
    header: () => i18n.t('admin.groups.col.name'),
  }),
  columnHelper.accessor(
    (row) => (row.group_type ? i18n.t(GROUP_TYPE_KEYS[row.group_type]) : EMPTY),
    { id: 'group_type', header: () => i18n.t('admin.groups.col.type') },
  ),
  columnHelper.display({
    id: 'location',
    header: () => i18n.t('admin.groups.col.location'),
    cell: ({ row }) => <LocationCell group={row.original} />,
  }),
  columnHelper.accessor((row) => row.wbs_code ?? EMPTY, {
    id: 'wbs_code',
    header: () => i18n.t('admin.groups.fields.wbsCode'),
  }),
  columnHelper.display({
    id: 'owners',
    header: () => i18n.t('admin.groups.col.owner'),
    cell: ({ row }) => <OwnerCell owners={row.original.owners} />,
  }),
  // Legacy's snapshot counter; groups born in the new stack have none, so the
  // placeholder is permanent for them, not a loading state.
  columnHelper.accessor((row) => row.total_member_count ?? EMPTY, {
    id: 'total_members',
    header: () => i18n.t('admin.groups.col.members'),
  }),
  columnHelper.accessor((row) => formatUtcDate(row.created_at), {
    id: 'created_at',
    header: () => i18n.t('admin.groups.col.created'),
  }),
  columnHelper.accessor((row) => formatUtcDate(row.last_activity), {
    id: 'last_activity',
    header: () => i18n.t('admin.groups.col.lastActivity'),
  }),
  columnHelper.display({
    id: 'actions',
    header: () => i18n.t('admin.groups.col.actions'),
    cell: ({ row }) => <ActionsCell group={row.original} />,
  }),
];

/**
 * Legacy's fourteen filters minus name, which is the search box. A hook because
 * the Country options come from the endpoint. Each key IS the wire parameter.
 * Group type has no "untyped" option: untyped groups are unreachable by filter,
 * as in legacy.
 */
function useGroupFilterFields(): FilterField[] {
  const { t } = useTranslation();
  const countries = useAdminGroupCountries();
  return [
    {
      key: 'group_type',
      label: t('admin.groups.col.type'),
      type: 'multiselect',
      options: GROUP_TYPES.map((value) => ({
        value,
        label: t(GROUP_TYPE_KEYS[value]),
      })),
    },
    {
      key: 'wbs_code',
      label: t('admin.groups.fields.wbsCode'),
      type: 'text',
    },
    // Rendered by the helper picker (`helperPickerKeys`). Owner matches an
    // `admin` membership, Users a plain `member` one.
    {
      key: 'group_owners',
      label: t('admin.groups.col.owner'),
      type: 'multiselect',
    },
    {
      key: 'group_users',
      label: t('admin.groups.filters.users'),
      type: 'multiselect',
    },
    {
      key: 'country',
      label: t('admin.groups.fields.country'),
      type: 'multiselect',
      options: countries.map((c) => ({ value: c.code, label: c.name })),
    },
    { key: 'city', label: t('admin.groups.fields.city'), type: 'text' },
    { key: 'state', label: t('admin.groups.fields.state'), type: 'text' },
    // Both compare the legacy counter, so both drop groups created since.
    {
      key: 'total_members_from',
      label: t('admin.groups.filters.membersFrom'),
      type: 'text',
      groupWith: 'total_members_to',
    },
    {
      key: 'total_members_to',
      label: t('admin.groups.filters.membersTo'),
      type: 'text',
    },
    {
      key: 'created_after',
      label: t('admin.groups.filters.createdAfter'),
      type: 'date',
      groupWith: 'created_before',
    },
    {
      key: 'created_before',
      label: t('admin.groups.filters.createdBefore'),
      type: 'date',
    },
    {
      key: 'last_activity_after',
      label: t('admin.groups.filters.activityAfter'),
      type: 'date',
      groupWith: 'last_activity_before',
    },
    {
      key: 'last_activity_before',
      label: t('admin.groups.filters.activityBefore'),
      type: 'date',
    },
  ];
}

// `GET /v3/groups` stays the header's dependency (it takes groups[0] from its
// id-ascending order), so the console reads its own admin list.
const GroupsList = createTablePage<AdminGroupItem, '/admin/groups/$groupId'>({
  name: 'admin.nav.groups',
  pageSize: PAGE_SIZE,
  searchable: true,
  searchPlaceholder: 'admin.groups.searchPlaceholder',
  defaultSort: { key: 'id', order: 'desc' },
  // An unrecognised sort key is ignored silently server-side, so these
  // spellings are the wire contract.
  sortKeys: {
    id: 'id',
    name: 'name',
    group_type: 'group_type',
    wbs_code: 'wbs_code',
    total_members: 'total_members',
    created_at: 'created_at',
    last_activity: 'last_activity',
  },
  filterFields: useGroupFilterFields,
  helperPickerKeys: ['group_owners', 'group_users'],
  useData: (_page, search, { paging, sort, filters }) => {
    const trimmed = search.trim();
    return api.listAdminGroups.useQuery({
      query: {
        limit: paging.limit,
        offset: paging.offset,
        ...(trimmed ? { search: trimmed } : {}),
        ...(sort ? { sort: sort.key, order: sort.order } : {}),
        ...adminGroupFilterQuery(filters),
      },
    });
  },
  columns,
  itemBaseUrl: '/admin/groups/$groupId',
  getParams: (row) => ({ groupId: String(row.id) }),
  actions: <CreateGroupButton />,
});

export const Route = createFileRoute('/admin/groups/')({
  staticData: { minTier: 'FRONT_END_SUPPORT' },
  component: GroupsList,
});
